import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import {
  kreirajPartnera,
  getKategorijePartnera,
  kreirajKategorijuPartnera,
  obrisiKategorijuPartnera
} from '../../lib/services/partnerService';

import DashboardSidebar from '../../components/navigation/DashboardSidebarAdmin';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';

const INITIAL_FORM_STATE = {
  naziv_partnera: '',
  tip_partnera: '', // čuva naziv ili id tipa/kategorije
  id_kategorije: '', // ako je izabrana custom kategorija iz baze
  pib_jib: '',
  pdv_broj: '',
  broj_registracije: '',
  adresa: '',
  grad: '',
  postanski_broj: '',
  drzava: '',
  email: '',
  telefon: '',
  web_stranica: '',
  bankovni_racun: '',
  naziv_banke: '',
  kontakt_osoba: '',
  kontakt_telefon: '',
  napomena: '',
};

// ── CATEGORY MANAGEMENT MODAL ────────────────────────────────────────────────
// Renderovan kroz React Portal na document.body, kako bi imao vlastiti
// stacking context i ostao ispravno prikazan iznad ostatka forme.
const CategoryManagementModal = ({
  kategorije,
  idAzienda,
  onClose,
  onCategoryChanged,
  t
}) => {
  const [novaKategorijaNaziv, setNovaKategorijaNaziv] = useState('');
  const [novaKategorijaOpis, setNovaKategorijaOpis] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [categoryError, setCategoryError] = useState('');

  const handleSaveCategory = async () => {
    const naziv = novaKategorijaNaziv.trim();
    if (!naziv) return;
    if (!idAzienda) {
      setCategoryError(t('create_partner.errors.no_company', 'Nije pronađena matična kompanija'));
      return;
    }

    setCategoryError('');
    setSavingCategory(true);
    const result = await kreirajKategorijuPartnera({
      naziv_kategorije: naziv,
      opis: novaKategorijaOpis.trim(),
      id_aziende: idAzienda
    });
    setSavingCategory(false);

    if (result.success) {
      setNovaKategorijaNaziv('');
      setNovaKategorijaOpis('');
      await onCategoryChanged();
    } else {
      setCategoryError(
        result.error === 'Kategorija sa ovim nazivom već postoji.'
          ? t('create_partner.category_modal.duplicate_error', 'Kategorija sa ovim nazivom već postoji.')
          : result.error || t('create_partner.category_modal.save_error', 'Greška pri kreiranju kategorije')
      );
    }
  };

  const handleDeleteCategory = async (katId) => {
    if (!idAzienda) return;
    if (!window.confirm(t('create_partner.category_modal.confirm_delete', 'Da li ste sigurni da želite obrisati ovu kategoriju?'))) return;

    setDeletingCategoryId(katId);
    const result = await obrisiKategorijuPartnera(katId, idAzienda);
    setDeletingCategoryId(null);

    if (result.success) {
      await onCategoryChanged();
    } else {
      setCategoryError(result.error || t('create_partner.category_modal.delete_error', 'Greška pri brisanju kategorije'));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-background/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border pb-3">
            <h3 className="text-base font-bold text-foreground flex items-center gap-2">
              <Icon name="Tags" size={18} className="text-primary" />
              {t('create_partner.category_modal.title', 'Upravljanje Kategorijama')}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Greška */}
          {categoryError && (
            <p className="text-red-500 text-xs p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              {categoryError}
            </p>
          )}

          {/* Lista postojećih kategorija */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('create_partner.category_modal.existing_list', 'Postojeće kategorije')}
            </label>
            {kategorije.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-2">
                {t('create_partner.category_modal.no_categories', 'Nema kreiranih kategorija.')}
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                {kategorije.map((kat) => (
                  <div
                    key={kat.id}
                    className="flex items-center justify-between gap-2 p-2.5 bg-muted/30 border border-border rounded-lg"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{kat.naziv_kategorije}</p>
                      {kat.opis && (
                        <p className="text-xs text-muted-foreground truncate">{kat.opis}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(kat.id)}
                      disabled={deletingCategoryId === kat.id}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors shrink-0 disabled:opacity-50"
                      title={t('create_partner.category_modal.delete', 'Obriši kategoriju')}
                    >
                      {deletingCategoryId === kat.id
                        ? <Icon name="Loader2" size={16} className="animate-spin" />
                        : <Icon name="Trash2" size={16} />
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          {/* Forma za dodavanje nove kategorije */}
          <div className="space-y-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('create_partner.category_modal.add_new', 'Dodaj novu kategoriju')}
            </label>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                {t('create_partner.category_modal.name_label', 'Naziv kategorije *')}
              </label>
              <input
                type="text"
                value={novaKategorijaNaziv}
                onChange={(e) => setNovaKategorijaNaziv(e.target.value)}
                placeholder={t('create_partner.category_modal.name_placeholder', 'npr. Podizvođač')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-border"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                {t('create_partner.category_modal.desc_label', 'Opis kategorije')}
              </label>
              <textarea
                rows={2}
                value={novaKategorijaOpis}
                onChange={(e) => setNovaKategorijaOpis(e.target.value)}
                placeholder={t('create_partner.category_modal.desc_placeholder', 'Opcionalni opis kategorije...')}
                className="w-full rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-border"
              />
            </div>

            <Button
              type="button"
              fullWidth
              onClick={handleSaveCategory}
              loading={savingCategory}
              disabled={!novaKategorijaNaziv.trim()}
            >
              {t('create_partner.category_modal.save_btn', 'Spremi kategoriju')}
            </Button>
          </div>

          {/* Zatvori */}
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('create_partner.category_modal.close', 'Zatvori')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CreaPartner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState({ id_azienda: null, nome_azienda: '' });
  const [kategorije, setKategorije] = useState([]);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Modal za upravljanje kategorijama (zamjenjuje stari inline unos)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  // Sidebar toggle — potreban za DashboardSidebar/MobileMenuButton layout
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const fetchKategorije = async (idAzienda) => {
    const targetId = idAzienda || adminData.id_azienda;
    if (!targetId) return;

    const katResult = await getKategorijePartnera(targetId);
    if (katResult.success) {
      setKategorije(katResult.data);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profilo } = await supabase
          .from('profili')
          .select('id_azienda, nome_azienda')
          .eq('id', user.id)
          .single();
        if (profilo) {
          setAdminData(profilo);
          await fetchKategorije(profilo.id_azienda);
        }
      }
    };

    loadInitialData();
  }, []);

  const handleTipChange = (e) => {
    const value = e.target.value;

    // Provjera da li je izabrana dinamička kategorija iz baze
    const selectedKat = kategorije.find((k) => String(k.id) === value);

    if (selectedKat) {
      setFormData((prev) => ({
        ...prev,
        tip_partnera: selectedKat.naziv_kategorije,
        id_kategorije: selectedKat.id,
      }));
    } else {
      // Standardni tipovi (dobavljac, klijent, obostrano)
      setFormData((prev) => ({
        ...prev,
        tip_partnera: value,
        id_kategorije: '',
      }));
    }

    if (errors.tip_partnera) {
      setErrors((prev) => ({ ...prev, tip_partnera: null }));
    }
  };

  // Nakon dodavanja ili brisanja kategorije u modalu, lista se osvježava.
  // Ako je selektovana kategorija u glavnoj formi bila obrisana, resetuje se izbor.
  const handleCategoryChanged = async () => {
    await fetchKategorije(adminData.id_azienda);
    setFormData((prev) => {
      if (!prev.id_kategorije) return prev;
      const stillExists = kategorije.some((k) => String(k.id) === String(prev.id_kategorije));
      if (stillExists) return prev;
      return { ...prev, tip_partnera: '', id_kategorije: '' };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.naziv_partnera.trim()) {
      newErrors.naziv_partnera = t('create_partner.errors.name_req', 'Naziv partnera je obavezan');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    if (!adminData.id_azienda) {
      alert(t('create_partner.errors.no_company', 'Nije pronađena matična kompanija'));
      return;
    }

    setIsLoading(true);
    setErrors({});

    const result = await kreirajPartnera({
      ...formData,
      id_aziende: adminData.id_azienda,
    });

    setIsLoading(false);

    if (result.success) {
      setIsSuccess(true);
      setFormData(INITIAL_FORM_STATE);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setIsSuccess(false), 5000);
    } else {
      setErrors({ general: result.error });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader userName={adminData?.nome_azienda || 'Admin'} />

      <main className="main-content with-sidebar py-10 px-4 md:px-8 mt-16">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Zaglavlje: Nazad + Moji Partneri (na desnoj strani) */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest"
            >
              <div className="p-2 bg-muted rounded-xl group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={16} />
              </div>
              {t('create_partner.back', 'Nazad')}
            </button>

            <button
              type="button"
              onClick={() => navigate('/lista-partner')}
              className="shrink-0 flex items-center justify-center gap-2 bg-primary/10 text-primary px-5 py-2.5 rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-primary/20 transition-all"
            >
              <Icon name="Users" size={16} />
              {t('create_partner.my_partners_btn', 'Moji Partneri')}
            </button>
          </div>

          {/* Naslov stranice — isti stil kao na Lista Partnera stranici */}
          <div>
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
              <Icon name="UserPlus" className="text-primary" size={32} />
              {t('create_partner.title', 'Novi Poslovni Partner')}
            </h1>
            <p className="text-xs text-muted-foreground mt-1 font-medium">
              {t('create_partner.subtitle', 'Dodavanje partnera za firmu:')}{' '}
              <strong>{adminData.nome_azienda}</strong>
            </p>
            <div className="h-1 w-20 bg-primary mt-3 rounded-full"></div>
          </div>

          {/* Kartica sa formom — sadržaj identičan originalu, nedirano */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
            <div className="p-6">
              {/* Notifikacija o uspjehu */}
              {isSuccess && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-600 rounded-lg flex items-center gap-3 animate-in fade-in zoom-in">
                  <Icon name="CheckCircle" size={20} />
                  <span>{t('create_partner.success_msg', 'Partner je uspješno sačuvan!')}</span>
                </div>
              )}

              {/* Opšta greška */}
              {errors.general && (
                <p className="text-red-500 text-sm mb-4 text-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  {errors.general}
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* SEKCIJA 1: Osnovni Podaci */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary border-b border-border pb-1">
                    {t('create_partner.sections.basic', '1. Osnovni Podaci')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      name="naziv_partnera"
                      label={t('create_partner.labels.naziv', 'Naziv Partnera *')}
                      value={formData.naziv_partnera}
                      onChange={handleChange}
                      error={errors.naziv_partnera}
                      required
                      placeholder={t('create_partner.placeholders.naziv', 'npr. ACME d.o.o.')}
                    />

                    {/* Tip / Kategorija Partnera */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-foreground">
                          {t('create_partner.labels.tip', 'Tip / Kategorija Partnera')}
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsCategoryModalOpen(true)}
                          className="flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-70 transition-all"
                        >
                          <Icon name="Settings2" size={14} />
                          {t('create_partner.manage_categories_btn', 'Upravljaj kategorijama')}
                        </button>
                      </div>
                      <select
                        name="tip_partnera"
                        value={formData.id_kategorije || formData.tip_partnera}
                        onChange={handleTipChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-border"
                      >
                        <option value="">{t('create_partner.select_placeholder', '-- Odaberi tip / kategoriju --')}</option>

                        {/* Default opcije */}
                        <optgroup label={t('create_partner.default_types_group', 'Podrazumijevani tipovi')}>
                          <option value="dobavljac">{t('create_partner.types.dobavljac', 'Dobavljač')}</option>
                          <option value="klijent">{t('create_partner.types.klijent', 'Klijent')}</option>
                          <option value="obostrano">{t('create_partner.types.obostrano', 'Klijent / Dobavljač')}</option>
                        </optgroup>

                        {/* Dinamičke kategorije iz baze */}
                        {kategorije.length > 0 && (
                          <optgroup label={t('create_partner.db_categories_group', 'Kategorije iz baze')}>
                            {kategorije.map((kat) => (
                              <option key={kat.id} value={kat.id}>
                                {kat.naziv_kategorije}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      name="pib_jib"
                      label={t('create_partner.labels.pib_jib', 'PIB / JIB')}
                      value={formData.pib_jib}
                      onChange={handleChange}
                      placeholder={t('create_partner.placeholders.pib_jib', 'npr. 4200000000000')}
                    />

                    <Input
                      name="pdv_broj"
                      label={t('create_partner.labels.pdv', 'PDV Broj')}
                      value={formData.pdv_broj}
                      onChange={handleChange}
                      placeholder={t('create_partner.placeholders.pdv', 'npr. 200000000000')}
                    />

                    <Input
                      name="broj_registracije"
                      label={t('create_partner.labels.broj_registracije', 'Broj Registracije (Matični broj)')}
                      value={formData.broj_registracije}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* SEKCIJA 2: Kontakt i Lokacija */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary border-b border-border pb-1">
                    {t('create_partner.sections.location', '2. Kontakt i Lokacija')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        name="adresa"
                        label={t('create_partner.labels.adresa', 'Adresa')}
                        value={formData.adresa}
                        onChange={handleChange}
                      />
                    </div>
                    <Input
                      name="postanski_broj"
                      label={t('create_partner.labels.postanski_broj', 'Poštanski Broj')}
                      value={formData.postanski_broj}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      name="grad"
                      label={t('create_partner.labels.grad', 'Grad')}
                      value={formData.grad}
                      onChange={handleChange}
                    />
                    <Input
                      name="drzava"
                      label={t('create_partner.labels.drzava', 'Država')}
                      value={formData.drzava}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      type="email"
                      name="email"
                      label={t('create_partner.labels.email', 'Email')}
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <Input
                      name="telefon"
                      label={t('create_partner.labels.telefon', 'Telefon')}
                      value={formData.telefon}
                      onChange={handleChange}
                    />
                    <Input
                      name="web_stranica"
                      label={t('create_partner.labels.web', 'Web Stranica')}
                      value={formData.web_stranica}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* SEKCIJA 3: Bankovni podaci i Kontakt osoba */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary border-b border-border pb-1">
                    {t('create_partner.sections.banking', '3. Banka i Kontakt Osoba')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      name="naziv_banke"
                      label={t('create_partner.labels.naziv_banke', 'Naziv Banke')}
                      value={formData.naziv_banke}
                      onChange={handleChange}
                    />
                    <Input
                      name="bankovni_racun"
                      label={t('create_partner.labels.bankovni_racun', 'Bankovni Račun (IBAN)')}
                      value={formData.bankovni_racun}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      name="kontakt_osoba"
                      label={t('create_partner.labels.kontakt_osoba', 'Kontakt Osoba')}
                      value={formData.kontakt_osoba}
                      onChange={handleChange}
                    />
                    <Input
                      name="kontakt_telefon"
                      label={t('create_partner.labels.kontakt_telefon', 'Telefon Kontakt Osobe')}
                      value={formData.kontakt_telefon}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* SEKCIJA 4: Napomene */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('create_partner.labels.napomena', 'Napomena')}
                  </label>
                  <textarea
                    name="napomena"
                    rows={3}
                    value={formData.napomena}
                    onChange={handleChange}
                    className="w-full rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-border"
                    placeholder={t('create_partner.placeholders.napomena', 'Unesite dodatne napomene o partneru...')}
                  />
                </div>

                {/* Dugme za slanje */}
                <div className="pt-4">
                  <Button type="submit" fullWidth loading={isLoading} size="lg">
                    {t('create_partner.submit_btn', 'Sačuvaj Partnera')}
                  </Button>
                </div>
              </form>
            </div>

            {/* Modal za upravljanje kategorijama */}
            {isCategoryModalOpen && (
              <CategoryManagementModal
                kategorije={kategorije}
                idAzienda={adminData.id_azienda}
                onClose={() => setIsCategoryModalOpen(false)}
                onCategoryChanged={handleCategoryChanged}
                t={t}
              />
            )}
          </div>
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default CreaPartner;